// src/tattoo/SkinTonePicker.jsx
import React from "react";
import { proxy, useSnapshot } from "valtio";
import { tattoo } from "./state";

const mem = proxy({
  key: "tt_custom_skin_swatches",
  get list() {
    try { return JSON.parse(localStorage.getItem(this.key) || "[]"); } catch {}
    return [];
  },
  set list(v) {
    try { localStorage.setItem(this.key, JSON.stringify(v.slice(0, 10))); } catch {}
  },
});

const clampHex = (hex) => {
  if (!hex) return "#ffffff";
  const m = String(hex).trim().match(/^#?([0-9a-f]{6})$/i);
  return "#" + (m ? m[1] : "ffffff").toLowerCase();
};

const SKINS = ["#a26b4e","#f2d3c1","#e6c0a8","#d9b39a","#c79a7b","#b57f5f","#9d6649","#7c4f38","#5b3827","#3e251a"];
function hexToRgb(h){const i=parseInt(h.slice(1),16);return {r:(i>>16)&255,g:(i>>8)&255,b:i&255};}
function dist(a,b){return (a.r-b.r)**2+(a.g-b.g)**2+(a.b-b.b)**2;}
function nearestNamed(hex){
  const rgb=hexToRgb(hex);
  let best=0, d=Infinity;
  for(let i=0;i<SKINS.length;i++){
    const dd=dist(rgb,hexToRgb(SKINS[i]));
    if(dd<d){d=dd;best=i;}
  }
  const names=["Porcelain","Almond","Sand","Honey","Caramel","Bronze","Chestnut","Walnut","Mahogany"];
  return {name:names[best], hex:SKINS[best]};
}

export default function SkinTonePicker() {
  const snap = useSnapshot(tattoo);
  const cur = clampHex(snap.skinColor || "#e6c0a8");
  const [val, setVal] = React.useState(cur);
  const [swatches, setSwatches] = React.useState(() => mem.list);

  React.useEffect(()=> setVal(cur), [cur]);

  const onPick = (hex) => {
    const h = clampHex(hex);
    setVal(h);
    tattoo.skinColor = h;
  };

  const onSave = () => {
    
    const h = clampHex(tattoo.skinColor || val);
    const next = [h, ...swatches.filter((x)=>x!==h)].slice(0,10);
    setSwatches(next);
    mem.list = next;
  };

  const onRemove = (hex) => {
    const next = swatches.filter((x)=>x!==hex);
    setSwatches(next);
    mem.list = next;
  };

  // If the text input is mid-edit (e.g. "#f2d3c") use current skin for the preview
  const validHex = /^#([0-9a-f]{6})$/i.test(val);
  const previewColor = validHex ? val : cur;

  const closest = nearestNamed(previewColor);

  return (
    <div className="skin-picker">
      {/* Saved custom swatches row */}
      <div className="swatches" style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:10}}>
        {swatches.length === 0 ? (
          <div className="tt-muted" style={{fontSize:12}}>No saved colors yet.</div>
        ) : swatches.map((c) => (
          <div key={c} style={{position:"relative", width:28, height:28}}>
            {/* Use an inner 'fill' so global .swatch rules cannot override the color */}
            <button
              type="button"
              className={`swatch custom ${cur===c ? "is-selected":""}`}
              aria-label={c}
              onClick={()=>onPick(c)}
              style={{
                width:"100%", height:"100%", borderRadius:8, border:"2px solid #333",
                padding:0, position:"relative", overflow:"hidden"
              }}
            >
              <span
                className="fill"
                style={{
                  position:"absolute", inset:0, background:c
                }}
              />
            </button>
            <button
              type="button"
              onClick={()=>onRemove(c)}
              title="Remove"
              style={{
                position:"absolute", top:-8, right:-8,
                width:18, height:18, borderRadius:9,
                border:"1px solid #444", background:"#151515",
                display:"flex", alignItems:"center", justifyContent:"center",
                color:"#bbb", fontSize:12, lineHeight:1, cursor:"pointer"
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Picker row */}
      <div className="tt-row" style={{gap:8, alignItems:"center"}}>
    {/* Tap the swatch to open native color picker (works on iPad) */}
    <div
      style={{
        position:"relative",
        width:28,height:28,borderRadius:6,border:"2px solid #333",
        background: previewColor, overflow:"hidden"
      }}
      title="Pick a custom color"
    >
      <input
        type="color"
        aria-label="Pick skin tone"
        // always pass a valid hex to the native picker
        value={clampHex(tattoo.skinColor || "#e6c0a8")}
        onChange={(e)=>onPick(e.target.value)}
        // iOS requirement: don't hide with display:none — overlay it
        style={{
          position:"absolute", inset:0,
          width:"100%", height:"100%",
          opacity:0, border:0, padding:0, margin:0,
          appearance:"none", cursor:"pointer"
        }}
      />
    </div>

        
        <button className="tt-btn" onClick={onSave}>Save current</button>
      </div>

      <div className="tt-muted" style={{marginTop:8,fontSize:12}}>
        Closest swatch: <strong>{closest.name}</strong> {closest.hex}
      </div>
    </div>
  );
}

export { SKINS };